import { RELATION_LABELS_PL } from "@anisync/types";
import type { EpisodeAvailability, RelatedTitle, TitleFull } from "@anisync/types";
import {
  CalendarClockIcon,
  DownloadIcon,
  ExternalLinkIcon,
  HardDriveDownloadIcon,
  LinkIcon,
  PlayIcon,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { AiringCountdown } from "../components/airing-countdown";
import { DownloadDialog } from "../components/download-dialog";
import {
  EpisodeList,
  EpisodeListSkeleton,
  type EpisodeProgressEntry,
  type PrimaryEpisode,
  primaryEpisode,
} from "../components/episode-list";
import { LibraryStatusPicker } from "../components/library-status-picker";
import { MapSourcesDialog } from "../components/map-sources-dialog";
import { TitleCard } from "../components/title-card";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardDescription, CardFooter, CardHeader } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "../components/ui/toast";
import { VersionPicker } from "../components/version-picker";
import { WrongVersionDialog } from "../components/wrong-version-button";
import {
  ApiError,
  type BatchProgressItem,
  addToRoomQueue,
  createRoom,
  deleteMedia,
  getBatchProgress,
  getEpisodes,
  getTitle,
  getTitleProgress,
  markWatched,
  prepareSeries,
} from "../lib/api";
import { formatClock } from "../lib/format";
import { trackMedia } from "../lib/media-tracker";
import { fadeIn } from "../lib/motion";
import { useSession } from "../lib/use-session";
import { useStartEpisode } from "../lib/use-start-episode";
import { cn } from "../lib/utils";

/** Takt odpytywania postępu. Sekunda to tempo, w jakim zmienia się pasek. */
const PROGRESS_TICK_MS = 1000;

const STATUS_LABELS: Record<string, string> = {
  finished: "Zakończone",
  releasing: "Emitowane",
  not_yet_released: "Zapowiedziane",
  cancelled: "Anulowane",
  hiatus: "Wstrzymane",
};

export function TitleScreen() {
  const { titleId = "" } = useParams();
  const navigate = useNavigate();
  const [title, setTitle] = useState<(TitleFull & { relations: RelatedTitle[] }) | null>(null);
  const [episodes, setEpisodes] = useState<EpisodeAvailability[] | null>(null);
  /** Kiedy poszło pytanie o odcinki — od tej chwili szkielet liczy czekanie. */
  const [episodesSince, setEpisodesSince] = useState(() => Date.now());
  const [error, setError] = useState<string | null>(null);
  const { session } = useSession();
  const startEpisode = useStartEpisode();
  const authenticated = session?.authenticated === true;
  // Rysowanie wejścia do mapowania. Odmowy pilnują strażnicy tras — ukrycie
  // przycisku nigdy nie jest ochroną, tylko uprzejmością wobec patrzącego.
  const isAdmin = session?.user?.isAdmin === true;
  const [mapOpen, setMapOpen] = useState(false);
  /** Numer odcinka, dla którego otwarto listę wersji; `null` = okno zamknięte. */
  const [versionsFor, setVersionsFor] = useState<number | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [progress, setProgress] = useState<Map<number, EpisodeProgressEntry>>(new Map());
  const [marking, setMarking] = useState<number | null>(null);
  const [preparing, setPreparing] = useState(false);
  /** Okno pobierania na dysk — otwierane z przycisku obok „Przygotuj serię". */
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [fetchProgress, setFetchProgress] = useState<Map<string, BatchProgressItem>>(new Map());
  /** Materiał, dla którego otwarto okno „Zła wersja” z menu wiersza. */
  const [wrongFor, setWrongFor] = useState<string | null>(null);

  /** „Frieren — odc. 9" — podpis powiadomień, rozpoznawalny poza tym ekranem. */
  const labelFor = (episode: number): string =>
    `${title !== null ? (title.titleEnglish ?? title.titleRomaji) : "Odcinek"} — odc. ${episode}`;

  /**
   * Przygotowanie całej serii.
   *
   * Dotąd trzeba było wejść w każdy odcinek osobno i czekać — przy dwunastu
   * odcinkach dwanaście wizyt na stronie odtwarzacza. Serwer dopisuje odcinki do
   * kolejki serii i pobiera je po jednym, następny po zakończeniu poprzedniego;
   * odcinek włączony z siatki wychodzi z kolejki od razu.
   */
  async function prepareAll(): Promise<void> {
    setPreparing(true);
    try {
      const result = await prepareSeries(titleId);
      const queued = result.queued.length;
      toast({
        tone: queued > 0 ? "success" : "info",
        title:
          queued > 0
            ? `Dodano do kolejki: ${queued} ${queued === 1 ? "odcinek" : queued < 5 ? "odcinki" : "odcinków"}`
            : "Nic do przygotowania",
        description:
          queued > 0
            ? "Pobierają się po kolei — następny po zakończeniu poprzedniego. Odcinek, który włączysz, idzie od razu."
            : "Wszystkie odcinki z kopiami są już na serwerze albo w drodze.",
        durationMs: 8000,
        ...(queued > 0
          ? { action: { label: "Kolejka", onClick: () => navigate("/kolejka") } }
          : {}),
      });
      // Odświeżamy dostępność, żeby wiersze od razu pokazały „w kolejce serii".
      getEpisodes(titleId)
        .then(setEpisodes)
        .catch(() => undefined);
    } catch (e: unknown) {
      toast({
        tone: "error",
        title: "Nie udało się przygotować serii",
        description: e instanceof ApiError ? e.message : "Spróbuj ponownie za chwilę.",
      });
    } finally {
      setPreparing(false);
    }
  }

  /**
   * Ręczne odhaczenie odcinka.
   *
   * Potrzebne, bo postęp powstawał WYŁĄCZNIE z odtwarzania u nas — a odcinek
   * obejrzany gdzie indziej albo przed założeniem konta nie miał jak trafić na
   * listę inaczej niż przez import całej listy z MyAnimeList. Bez tego biblioteka
   * kłamie w drugą stronę: pokazuje jako nieobejrzane to, co widzieliśmy.
   *
   * Stan lokalny aktualizujemy PO odpowiedzi serwera, nie przed nią. Odhaczenie
   * pociąga za sobą zmianę statusu serii i datę ukończenia, więc pokazanie
   * ptaszka przed potwierdzeniem znaczyłoby czasem pokazanie czegoś, czego nie ma.
   */
  async function toggleWatched(episode: number, watched: boolean): Promise<void> {
    setMarking(episode);
    try {
      await markWatched(titleId, episode, watched);
      setProgress((prev) => {
        const next = new Map(prev);
        const existing = next.get(episode);
        next.set(episode, {
          episodeNumber: episode,
          // Zdjęcie ptaszka zeruje też pozycję, tak jak robi to serwer — inaczej
          // kafel pokazywałby „Od 12:34" dla odcinka oznaczonego jako nieobejrzany.
          positionSec: watched ? (existing?.positionSec ?? 0) : 0,
          durationSec: existing?.durationSec ?? 0,
          completed: watched,
        });
        return next;
      });
    } catch (e: unknown) {
      toast({
        tone: "error",
        title: labelFor(episode),
        description: e instanceof ApiError ? e.message : "Nie udało się zapisać obejrzenia",
      });
    } finally {
      setMarking(null);
    }
  }

  /**
   * Uruchamia odcinek. Serwer odpowiada od razu, a kopii szuka w tle, więc
   * przechodzimy do odtwarzacza bez czekania — a powiadomienie potwierdza
   * kliknięcie, zanim cokolwiek odpowie, i prowadzi odcinek aż do „Oglądaj".
   */
  function play(
    episode: number,
    prefer?: { sourceId?: string; preferHost?: string; preferEmbedUrl?: string },
    knownState?: EpisodeAvailability["mediaState"],
  ): void {
    void startEpisode({
      titleId,
      episode,
      label: labelFor(episode),
      ...(prefer !== undefined ? { prefer } : {}),
      ...(knownState !== undefined ? { knownState } : {}),
    });
  }

  /**
   * Usuwa materiał odcinka — także taki, który właśnie się pobiera.
   *
   * Serwer przerywa kaskadę przed skasowaniem wiersza, więc to jest zarazem
   * „przerwij pobieranie". Dwie czynności pod jednym przyciskiem, bo z punktu
   * widzenia klikającego są jedną: „nie chcę tego pliku".
   *
   * Po usunięciu odświeżamy siatkę, żeby kafel wrócił do stanu „do pobrania" —
   * bez tego zostawałby ślad po materiale, którego już nie ma, i kolejne
   * kliknięcie „Odtwórz" wyglądałoby na nieskuteczne.
   */
  async function removeEpisode(mediaFileId: string): Promise<void> {
    setRemoving(mediaFileId);
    try {
      await deleteMedia(mediaFileId);
      reloadEpisodes();
    } catch (e: unknown) {
      toast({
        tone: "error",
        title: "Nie udało się usunąć materiału",
        description: e instanceof ApiError ? e.message : "Spróbuj ponownie za chwilę.",
      });
    } finally {
      setRemoving(null);
    }
  }

  /**
   * Wspólne oglądanie: pokój powstaje z odcinkiem już w kolejce.
   *
   * Kolejność ma znaczenie — najpierw pokój, potem pozycja, dopiero na końcu
   * przejście. Wejście do pustego pokoju wymagałoby od użytkownika drugiego
   * kliknięcia, żeby cokolwiek zaczęło się dziać.
   *
   * **Pokój nosi nazwę serii, nie odcinka, i jest brany ponownie.** Wcześniej
   * każde kliknięcie zakładało nowy pokój z numerem odcinka w nazwie, więc trzy
   * podejścia do jednego odcinka dawały trzy pokoje o niemal identycznych adresach
   * (na produkcji zostało po tym dziesięć porzuconych). Jeden pokój na serię
   * odpowiada temu, jak się ogląda: wieczór z jedną serią, kolejne odcinki
   * w kolejce.
   */
  async function watchTogether(episode: number): Promise<void> {
    const toastId = toast({
      tone: "loading",
      title: labelFor(episode),
      description: "Przygotowuję pokój…",
    });
    try {
      const label = title !== null ? (title.titleEnglish ?? title.titleRomaji) : "Pokój";
      const room = await createRoom({ name: label, reuseEmpty: true });
      await addToRoomQueue(room.slug, { titleId, episodeNumber: episode });
      toast({
        id: toastId,
        tone: "success",
        title: labelFor(episode),
        description: "Odcinek jest w kolejce pokoju — pokój pokaże postęp pobierania.",
        durationMs: 4000,
      });
      navigate(`/pokoj/${room.slug}`);
    } catch (e: unknown) {
      toast({
        id: toastId,
        tone: "error",
        title: labelFor(episode),
        description: e instanceof ApiError ? e.message : "Nie udało się utworzyć pokoju",
      });
    }
  }

  /**
   * Dostępność odcinków pytamy osobno: wymaga odpytania źródeł, więc jest
   * wolniejsza i nie może blokować pokazania samych metadanych.
   *
   * Wydzielone z efektu, bo woła to także okno mapowania — przypięcie źródła bez
   * odświeżenia siatki wyglądałoby jak kliknięcie, które nic nie zrobiło.
   */
  const reloadEpisodes = useCallback(() => {
    setEpisodes(null);
    setEpisodesSince(Date.now());
    getEpisodes(titleId)
      .then(setEpisodes)
      .catch(() => setEpisodes([]));
  }, [titleId]);

  useEffect(() => {
    setTitle(null);
    setError(null);

    getTitle(titleId)
      .then(setTitle)
      .catch((e: unknown) =>
        setError(e instanceof ApiError ? e.message : "Nie udało się wczytać tytułu"),
      );

    reloadEpisodes();
  }, [titleId, reloadEpisodes]);

  useEffect(() => {
    if (!authenticated) {
      setProgress(new Map());
      return;
    }
    let cancelled = false;
    getTitleProgress(titleId)
      .then((data) => {
        if (cancelled) return;
        setProgress(new Map(data.episodes.map((e) => [e.episodeNumber, e])));
      })
      // Brak postępu to normalny stan, nie awaria — lista działa bez niego.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [titleId, authenticated]);

  /**
   * Postęp pobierania odcinków, które są w drodze.
   *
   * Zgłoszenie z użycia: przy „przygotuj całą serię" widać było tylko słowo
   * `acquiring`, bez żadnej informacji o procesie. Postęp istniał na serwerze,
   * ale nikt o niego nie pytał.
   *
   * Jedno żądanie na wszystkie pozycje w drodze, nie jedno na kafel: przy
   * dwudziestu odcinkach byłoby dwadzieścia żądań na takt. Odpytujemy tylko gdy
   * jest co odpytywać, i przestajemy, gdy wszystko doszło do stanu końcowego —
   * otwarta karta nie ma generować ruchu bez powodu.
   */
  const pendingIds = (episodes ?? [])
    .filter(
      (ep) => ep.mediaFileId !== null && ep.mediaState !== "ready" && ep.mediaState !== "failed",
    )
    .map((ep) => ep.mediaFileId as string);
  const pendingKey = pendingIds.join(",");

  useEffect(() => {
    if (pendingKey === "") {
      setFetchProgress(new Map());
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = async (): Promise<void> => {
      try {
        const data = await getBatchProgress(pendingKey.split(","));
        if (cancelled) return;
        setFetchProgress(new Map(data.items.map((item) => [item.mediaFileId, item])));

        // Gdy wszystko doszło do końca, odświeżamy listę odcinków: znaczniki
        // „pobrany" biorą się z niej, nie z postępu.
        if (data.items.every((item) => item.state === "ready" || item.state === "failed")) {
          getEpisodes(titleId)
            .then((list) => {
              if (!cancelled) setEpisodes(list);
            })
            .catch(() => undefined);
          return;
        }
      } catch {
        // Pojedyncza nieudana próba nie jest awarią — kolejny takt ją powtórzy.
      }
      if (!cancelled) timer = setTimeout(() => void tick(), PROGRESS_TICK_MS);
    };

    void tick();
    return () => {
      cancelled = true;
      if (timer !== null) clearTimeout(timer);
    };
  }, [pendingKey, titleId]);

  if (error !== null) {
    return (
      <div className="flex flex-col gap-4">
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <Button variant="link" className="self-start px-0" asChild>
          <Link to="/szukaj">Wróć do wyszukiwania</Link>
        </Button>
      </div>
    );
  }

  if (title === null) {
    return <TitleSkeleton episodesSince={episodesSince} />;
  }

  const accent = title.colorHex ?? "var(--color-primary)";
  const heading = title.titleEnglish ?? title.titleRomaji;
  const meta = [
    title.seasonYear !== null ? String(title.seasonYear) : null,
    title.status !== null ? (STATUS_LABELS[title.status] ?? title.status) : null,
    title.episodesTotal !== null ? `${title.episodesTotal} odc.` : null,
    title.durationMin !== null ? `${title.durationMin} min` : null,
    title.averageScore !== null ? `${title.averageScore}/100` : null,
  ].filter((x): x is string => x !== null);
  const primary = episodes !== null ? primaryEpisode(episodes, progress) : null;

  return (
    <div className="flex flex-col gap-8">
      {/*
        Nagłówek na pełną szerokość okna i pod pasek górny — ten sam zabieg co
        w karuzeli na stronie głównej. Baner z AniLista jest panoramiczny, więc
        w ramce o stałej szerokości tracił większość kadru.
      */}
      <section className="bleed-edge relative -mt-5 overflow-hidden bg-card">
        {title.bannerUrl !== null && (
          <img
            {...fadeIn}
            src={title.bannerUrl}
            alt=""
            className="h-56 w-full object-cover md:h-72 xl:h-80"
          />
        )}
        {/* Podwójny scrim: pionowy pod tekst, poziomy pod kolumnę z treścią. */}
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, var(--color-background) 0%, color-mix(in oklab, var(--color-background) 70%, transparent) 45%, transparent 80%)," +
              "linear-gradient(0deg, var(--color-background) 0%, transparent 70%)",
          }}
        />
        {/* Treść nachodzi na dół banera: osobno, pod nim, cały nagłówek zajmował
            na szerokim ekranie pół wysokości okna, zanim zaczynała się lista. */}
        <div
          className={cn(
            "page-gutter relative flex gap-6 py-6 md:items-end md:gap-8 md:py-8",
            title.bannerUrl !== null && "-mt-20 md:-mt-32 xl:-mt-40",
          )}
        >
          {/* Ramka trzyma miejsce i kolor karty, dopóki okładka się nie wczyta —
              sam obraz z `fadeIn` jest do tego czasu przezroczysty razem z tłem. */}
          {title.coverUrl !== null && (
            <div
              className="hidden aspect-[2/3] w-32 shrink-0 overflow-hidden rounded-lg border border-border bg-secondary sm:block md:w-40 xl:w-48"
              style={{ boxShadow: `0 0 3rem -1rem ${accent}` }}
            >
              <img {...fadeIn} src={title.coverUrl} alt="" className="size-full object-cover" />
            </div>
          )}
          <div className="flex min-w-0 flex-col gap-2">
            <h1 className="text-2xl leading-tight font-bold">{heading}</h1>
            {title.titleRomaji !== heading && (
              <p className="text-muted-foreground">{title.titleRomaji}</p>
            )}
            {title.titleNative !== null && (
              <p className="text-sm text-muted-foreground">{title.titleNative}</p>
            )}
            {meta.length > 0 && <p className="text-sm text-muted-foreground">{meta.join(" · ")}</p>}

            {/* Termin następnego odcinka leżał w bazie i nie był nigdzie widoczny. */}
            {title.nextAiringAt !== null && (
              <p className="flex items-center gap-1.5 text-sm text-accent">
                <CalendarClockIcon className="size-4" />
                <AiringCountdown airingAt={title.nextAiringAt} episode={title.nextAiringEpisode} />
              </p>
            )}
            {(primary !== null || authenticated || episodes === null) && (
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2">
                {/* „Co teraz włączyć" bez szukania po liście: przy dwudziestu
                    czterech kartach odcinek, na którym się skończyło, bywa poza
                    ekranem. Do przyjścia listy stoi tu szkielet przycisku —
                    inaczej nagłówek podskakiwał w górę, kiedy przycisk dochodził. */}
                {episodes === null && <Skeleton className="h-9 w-44" />}
                {primary !== null && (
                  <Button
                    variant="hero"
                    onClick={() =>
                      play(primary.episode.episodeNumber, undefined, primary.episode.mediaState)
                    }
                  >
                    <PlayIcon />
                    {primaryLabel(primary)}
                  </Button>
                )}
                {authenticated && <LibraryStatusPicker titleId={titleId} />}
              </div>
            )}
            {title.genres.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-2">
                {title.genres.map((g) => (
                  <Badge key={g} variant="outline">
                    {g}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/*
        Od 2xl dwie kolumny: odcinki jako główna treść, opis i powiązane obok.
        Wcześniej wszystko szło jednym słupem przez całe okno — opis, pas
        powiązanych i dopiero pod nimi odcinki, czyli na monitorze 4K lista,
        po którą się przychodzi, zaczynała się pod krawędzią ekranu. Węższy ekran
        zostaje przy jednym słupie: zwinięty opis, odcinki, powiązane na końcu
        (tam, gdzie się kończy serię i szuka następnej).
      */}
      <div className="grid gap-8 2xl:grid-cols-[minmax(0,1fr)_minmax(20rem,26rem)] 2xl:items-start">
        <aside className="flex min-w-0 flex-col gap-5 2xl:col-start-2 2xl:row-start-1">
          {title.description !== null && (
            <TitleDescription text={title.description.replace(/<[^>]+>/g, "")} />
          )}

          {title.trailer !== null && (
            /* Odnośnik na zewnątrz, NIE ramka `iframe`.
               Zasada projektu mówi „nigdy nie odtwarzamy zdalnego adresu" i tu jest
               słabsza, niż przy odcinku — zwiastun nie potrzebuje synchronizacji,
               postępu ani napisów — ale wstawienie odtwarzacza YouTube'a znaczy
               wpuszczenie na stronę cudzego skryptu i śledzenia widza tam, gdzie cała
               reszta jest serwowana z tego hosta. Trzy słowa i nowa karta są uczciwszą
               odpowiedzią niż udawanie, że zwiastun jest częścią naszego odtwarzacza. */
            <Button variant="outline" size="sm" className="self-start" asChild>
              <a href={title.trailer.url} target="_blank" rel="noreferrer noopener">
                <ExternalLinkIcon />
                Zwiastun w nowej karcie
              </a>
            </Button>
          )}

          {/* Powiązane w bocznej kolumnie jako lista, nie pas kafli: pas przewijany
              w poziomie w kolumnie szerokości dwóch okładek pokazywałby półtorej. */}
          {title.relations.length > 0 && (
            <section className="hidden flex-col gap-2 2xl:flex">
              <h2 className="text-sm font-semibold tracking-wide text-muted-foreground uppercase">
                Powiązane
              </h2>
              <ul className="flex flex-col gap-1">
                {title.relations.map((relation) => (
                  <li key={`${relation.relationType}-${relation.title.id}`}>
                    <RelatedRow relation={relation} />
                  </li>
                ))}
              </ul>
            </section>
          )}
        </aside>

        <section className="min-w-0 2xl:col-start-1 2xl:row-start-1">
          {/* Kart tyle, ile odcinków zapowiada katalog (z sufitem), żeby lista nie
              skoczyła po wczytaniu z trzech kart do dwudziestu czterech. */}
          {episodes === null && (
            <EpisodeListSkeleton
              count={Math.min(title.episodesTotal ?? 12, 24)}
              since={episodesSince}
            />
          )}

          {episodes !== null && episodes.length === 0 && (
            <h2 className="mb-3 text-lg font-semibold">Odcinki</h2>
          )}
          {episodes !== null && episodes.length === 0 && (
            <Card>
              <CardHeader>
                <CardDescription>
                  {/*
                    Rada zależy od tego, czy patrzący MOŻE ją wykonać.
                    Poprzednia wersja mówiła każdemu „zmapuj pozycję ręcznie w panelu",
                    co dla widza bez uprawnień było poleceniem niewykonalnym, a dla
                    administratora prowadziło do ekranu z kolejką przeglądu — w której
                    tytuł nieprzysłany przez żadne źródło nie wisi w ogóle.
                  */}
                  {isAdmin
                    ? "Żadne włączone źródło nie ma jeszcze tego tytułu. Wskaż pozycję w serwisie ręcznie albo odśwież katalog źródła."
                    : "Żadne włączone źródło nie ma jeszcze tego tytułu. Poproś administratora o wskazanie pozycji w serwisie."}
                </CardDescription>
              </CardHeader>
              <CardFooter className="flex flex-wrap gap-2">
                {isAdmin && (
                  <Button variant="secondary" size="sm" onClick={() => setMapOpen(true)}>
                    <LinkIcon />
                    Wskaż źródło
                  </Button>
                )}
                <Button variant="outline" size="sm" asChild>
                  <Link to="/zrodla">Przejdź do źródeł</Link>
                </Button>
              </CardFooter>
            </Card>
          )}

          {episodes !== null && episodes.length > 0 && (
            <EpisodeList
              episodes={episodes}
              progress={progress}
              live={fetchProgress}
              authenticated={authenticated}
              removing={removing}
              marking={marking}
              onPlay={(ep) => play(ep.episodeNumber, undefined, ep.mediaState)}
              onVersions={setVersionsFor}
              onTogether={(episode) => void watchTogether(episode)}
              onToggleWatched={(episode, watched) => void toggleWatched(episode, watched)}
              onRemove={(mediaFileId) => void removeEpisode(mediaFileId)}
              onWrongVersion={setWrongFor}
              actions={
                authenticated ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => void prepareAll()}
                      disabled={preparing}
                      title="Pobiera odcinki na serwer po kolei, jeden po drugim"
                    >
                      <DownloadIcon />
                      {preparing ? "Kolejkuję…" : "Przygotuj całą serię"}
                    </Button>
                    {/*
                    „Przygotuj" zdobywa materiał na SERWER, „Pobierz na dysk" zabiera go
                    do siebie. Dwie różne rzeczy pod dwoma przyciskami, obok siebie, bo
                    w praktyce jedno następuje po drugim.
                  */}
                    <Button variant="outline" size="sm" onClick={() => setDownloadOpen(true)}>
                      <HardDriveDownloadIcon />
                      Pobierz na dysk
                    </Button>
                    {/*
                    Przy KAŻDYM tytule, nie tylko przy pustej liście: najczęstsza usterka
                    mapowania to wskazanie błędne — lista pełna odcinków innej serii.
                  */}
                    {isAdmin && (
                      <Button variant="ghost" size="sm" onClick={() => setMapOpen(true)}>
                        <LinkIcon />
                        Źródła tytułu
                      </Button>
                    )}
                  </>
                ) : undefined
              }
            />
          )}
        </section>
      </div>

      {/*
        Powiązane tytuły. Dotąd przejście do drugiego sezonu wymagało ponownego
        szukania — a to najczęstsza rzecz, jaką robi się po skończeniu serii.
        Etykieta jest NAD kaflem, nie w nim: rodzaj powiązania („Kontynuacja")
        jest ważniejszy od tytułu przy wybieraniu, co dalej. Od 2xl ta sama treść
        stoi w bocznej kolumnie.
      */}
      {title.relations.length > 0 && (
        <section className="min-w-0 2xl:hidden">
          <h2 className="mb-3 text-sm font-semibold tracking-wide text-muted-foreground uppercase">
            Powiązane
          </h2>
          <div className="rail-scroll bleed pb-2">
            <div className="flex gap-4">
              {title.relations.map((relation) => (
                <div
                  key={`${relation.relationType}-${relation.title.id}`}
                  className="flex shrink-0 flex-col gap-1.5"
                >
                  <span className="text-xs text-accent">
                    {RELATION_LABELS_PL[relation.relationType] ?? relation.relationType}
                  </span>
                  <TitleCard title={relation.title} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/*
        Okno pobierania stoi na końcu drzewa, poza sekcjami: jest warstwą nakładaną,
        a nie częścią układu strony. Manifest pobiera samo, dopiero po otwarciu.
      */}
      {authenticated && (
        <DownloadDialog titleId={titleId} open={downloadOpen} onOpenChange={setDownloadOpen} />
      )}

      {/* Tu samo `isAdmin` wystarcza jako warunek RYSOWANIA; odmowy pilnują
          strażnicy tras po stronie serwera, bo ukrycie przycisku nie jest ochroną. */}
      {/*
        Lista wersji montuje się dopiero po otwarciu i pyta źródła sama.
        Trzymanie jej dla każdego kafla naraz znaczyłoby dwadzieścia pięć rund
        żądań do serwisów przy wejściu na stronę tytułu.
      */}
      {authenticated && versionsFor !== null && (
        <VersionPicker
          titleId={titleId}
          episodeNumber={versionsFor}
          open={versionsFor !== null}
          onOpenChange={(next) => {
            if (!next) setVersionsFor(null);
          }}
          onPick={(option) => {
            /**
             * Adres wersji, nie host. Z samym hostem docchi z trzema kopiami na
             * `vidara.to` dawało zawsze pierwszą — „Pobierz” przy drugiej
             * pobierało tę samą wersję, przed którą widz właśnie uciekał.
             */
            play(versionsFor, { sourceId: option.sourceId, preferEmbedUrl: option.embedUrl });
          }}
        />
      )}

      {/* Jedno okno „Zła wersja” dla całej listy, otwierane z menu wiersza. */}
      {authenticated && wrongFor !== null && (
        <WrongVersionDialog
          mediaFileId={wrongFor}
          open={wrongFor !== null}
          onOpenChange={(next) => {
            if (!next) setWrongFor(null);
          }}
          onDone={(result) => {
            const episode = episodes?.find((e) => e.mediaFileId === wrongFor)?.episodeNumber;
            setWrongFor(null);
            reloadEpisodes();
            if (episode === undefined) return;
            if (result.kept) {
              toast({
                tone: "info",
                title: labelFor(episode),
                description:
                  "Kopia zapamiętana jako zła. Plik zostaje, bo ktoś ogląda go w pokoju — następne pobranie weźmie inną.",
                durationMs: 8000,
              });
              return;
            }
            // Serwer szuka już następnej kopii — powiadomienie prowadzi ją jak zwykłe
            // uruchomienie, bo z punktu widzenia widza to jest to samo czekanie.
            const label = labelFor(episode);
            const toastId = toast({
              tone: "loading",
              title: label,
              description: "Kopia odrzucona. Szukam innej w źródłach…",
              progress: null,
            });
            trackMedia({
              mediaFileId: result.mediaFileId,
              label,
              href: `/odtwarzacz/${result.mediaFileId}?tytul=${titleId}&odcinek=${episode}`,
              toastId,
            });
          }}
        />
      )}

      {isAdmin && title !== null && (
        <MapSourcesDialog
          titleId={titleId}
          titleRomaji={title.titleRomaji}
          open={mapOpen}
          onOpenChange={setMapOpen}
          onChanged={reloadEpisodes}
        />
      )}
    </div>
  );
}

/** Podpis głównego przycisku — mówi, KTÓRY odcinek ruszy, zanim się kliknie. */
function primaryLabel(primary: PrimaryEpisode): string {
  const n = primary.episode.episodeNumber;
  if (primary.kind === "resume" && primary.resumeAt !== null) {
    return `Kontynuuj odc. ${n} od ${formatClock(primary.resumeAt)}`;
  }
  if (primary.kind === "next") return `Dalej: odcinek ${n}`;
  return `Odtwórz odcinek ${n}`;
}

/** Od ilu znaków opis dostaje „Pokaż cały opis". Krótszy mieści się w zwiniętym. */
const DESCRIPTION_FOLD_CHARS = 320;

/**
 * Opis zwinięty do kilku linii.
 *
 * Opisy z AniLista mają po kilkaset słów, a stały nad listą odcinków w całości:
 * na telefonie lista zaczynała się dwa ekrany niżej. Zwinięcie jest tylko
 * wizualne — czytnik ekranu dostaje cały tekst, a przycisk to jawne rozwinięcie.
 */
function TitleDescription({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const foldable = text.length > DESCRIPTION_FOLD_CHARS;
  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-sm font-semibold tracking-wide text-muted-foreground uppercase">Opis</h2>
      {/* Opis z katalogu bywa z prostym HTML-em; wołający zdejmuje znaczniki,
          bo wstawianie obcego znacznika do DOM byłoby otwartą furtką. */}
      <p
        className={cn(
          "max-w-3xl leading-relaxed whitespace-pre-line text-muted-foreground",
          foldable && !open && "line-clamp-4 2xl:line-clamp-[12]",
        )}
      >
        {text}
      </p>
      {foldable && (
        <Button
          variant="link"
          size="sm"
          className="self-start px-0"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? "Zwiń opis" : "Pokaż cały opis"}
        </Button>
      )}
    </section>
  );
}

/** Powiązany tytuł w bocznej kolumnie: okładka, rodzaj powiązania, nazwa. */
function RelatedRow({ relation }: { relation: RelatedTitle }) {
  const name = relation.title.titleEnglish ?? relation.title.titleRomaji;
  return (
    <Link
      to={`/tytul/${relation.title.id}`}
      className="focus-ring-own flex items-center gap-3 rounded-lg p-1.5 outline-none transition-colors duration-150 hover:bg-secondary/60 focus-visible:ring-[0.1875rem] focus-visible:ring-ring/50"
    >
      {relation.title.coverUrl !== null ? (
        <img
          {...fadeIn}
          src={relation.title.coverUrl}
          alt=""
          loading="lazy"
          className="h-16 w-11 shrink-0 rounded-md bg-secondary object-cover"
        />
      ) : (
        <span className="h-16 w-11 shrink-0 rounded-md bg-secondary" />
      )}
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="text-xs text-accent">
          {RELATION_LABELS_PL[relation.relationType] ?? relation.relationType}
        </span>
        <span className="line-clamp-2 text-sm font-medium">{name}</span>
      </span>
    </Link>
  );
}

/**
 * Kształt strony tytułu na czas wczytywania metadanych.
 *
 * Te same wymiary co gotowa strona — baner, okładka nachodząca na jego dół,
 * trzy linie tekstu i przycisk — więc po wczytaniu nic nie przeskakuje,
 * tylko się wypełnia. Wcześniej stało tu samo „Wczytuję…" w lewym górnym rogu.
 */
function TitleSkeleton({ episodesSince }: { episodesSince: number }) {
  return (
    <div className="flex flex-col gap-8" aria-busy="true" aria-label="Wczytuję tytuł">
      {/* Baner jako karta, nie szkielet — okładka i linie nachodzą na jego dół
          i w tym samym kolorze zlałyby się z nim. */}
      <div className="bleed-edge -mt-5 h-56 bg-card md:h-72 xl:h-80" />
      <div className="relative -mt-20 flex gap-6 md:-mt-32 md:items-end md:gap-8 xl:-mt-40">
        <Skeleton className="hidden aspect-[2/3] w-32 shrink-0 rounded-lg border border-border sm:block md:w-40 xl:w-48" />
        <div className="flex min-w-0 flex-1 flex-col gap-3 pb-1">
          <Skeleton className="h-8 w-2/3 max-w-xl" />
          <Skeleton className="h-4 w-1/3 max-w-xs" />
          <Skeleton className="h-4 w-1/2 max-w-sm" />
          <Skeleton className="mt-2 h-9 w-48" />
        </div>
      </div>
      <EpisodeListSkeleton count={12} since={episodesSince} />
    </div>
  );
}
